import { Controller as BaseController } from "@hotwired/stimulus"
import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"

import UploadImage from "./upload_image"

export class Controller extends BaseController {
  static targets = ["input"]

  connect() {
    this.newEditor()
    document.addEventListener("turbo:render", this.newEditor.bind(this))
  }

  newEditor() {
    let formInput = this.inputTarget
    if (formInput.hidden) return

    this.editor = new Editor({
      element: this.element,
      extensions: [
        StarterKit,
        UploadImage.configure({
          uploadFn: async (file) => {
            const formData = new FormData()
            formData.append("file", file)

            const csrfToken = document.querySelector(
              'meta[name="csrf-token"]'
            )?.content

            const response = await fetch("/media", {
              method: "POST",
              headers: {
                "X-CSRF-Token": csrfToken,
              },
              body: formData,
            })

            if (!response.ok) {
              throw new Error(`Upload failed: ${response.status}`)
            }

            const data = await response.json()
            return data.url
          },
        }),
      ],
      content: formInput.value,
      onUpdate: ({ editor }) => {
        const content = editor.isEmpty ? "" : editor.getHTML()
        formInput.value = content
      },
      editorProps: {
        attributes: {
          class: formInput.classList,
          "aria-label": "Rich text editor",
          rows: 5,
        },
      },
    })

    formInput.hidden = true
  }

  addImage() {
    this.editor.chain().focus().addImage().run()
  }

  openGallery() {
    this.fetchAttachments()
      .then((attachments) => {
        this.showGalleryModal(attachments)
      })
      .catch((error) => {
        console.error("Failed to fetch attachments:", error)
      })
  }

  async fetchAttachments() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const response = await fetch("/media", {
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrfToken,
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch attachments: ${response.status}`)
    }

    const data = await response.json()
    return data.attachments
  }

  showGalleryModal(attachments) {
    // Create modal element
    let modal = document.querySelector("[data-controller='gallery-modal']")
    if (!modal) {
      modal = document.createElement("div")
    }

    modal.innerHTML = `
      <div data-controller="gallery-modal"
        data-gallery-modal-attachments-value='${JSON.stringify(attachments).replace(/'/g, "&apos;")}'
      </div>
    `

    document.body.appendChild(modal)
    this.galleryModal = modal
  }

  closeGalleryModal() {
    if (this.galleryModal) {
      this.galleryModal.remove()
      this.galleryModal = null
    }

    // TODO: Dead gallery modal disables scrolling. This is a temporary fix as
    // it allows other modals to be scrollable.
    document.documentElement.style.overflowY = "auto"
  }

  insertImageFromGallery(event) {
    this.insertImage(event.detail.url)
    this.closeGalleryModal()
  }

  insertImage(url) {
    this.editor.chain().focus().setImage({ src: url }).run()
  }

  toggleBold() {
    this.editor.chain().focus().toggleBold().run()
  }

  toggleItalics() {
    this.editor.chain().focus().toggleItalic().run()
  }

  toggleUnderline() {
    this.editor.chain().focus().toggleUnderline().run()
  }

  toggleHeadingOne() {
    this.editor.chain().focus().toggleHeading({ level: 1 }).run()
  }

  toggleHeadingTwo() {
    this.editor.chain().focus().toggleHeading({ level: 2 }).run()
  }

  toggleHeadingThree() {
    this.editor.chain().focus().toggleHeading({ level: 3 }).run()
  }

  toggleBulletList() {
    this.editor.chain().focus().toggleBulletList().run()
  }

  toggleOrderedList() {
    this.editor.chain().focus().toggleOrderedList().run()
  }

  toggleHorizontalRule() {
    this.editor.chain().focus().setHorizontalRule().run()
  }

  toggleBlockQuote() {
    this.editor.chain().focus().toggleBlockquote().run()
  }
}
